import { type FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button/Button'
import InlineDatePicker from 'components/UI/InlineDatePicker/InlineDatePicker'
import InlineTimePicker from 'components/UI/InlineTimePicker/InlineTimePicker'
import { useModalContext } from 'context/modalContext'

import './_dateModal.scss'

import { getScheduledContent } from 'services/endpoints/schedule'
import { type ISchedule, type postType } from 'types/interfaces/ITypes'

import ScheduleContent from '../ScheduleContent/ScheduleContent'

const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

const getNowWithoutSeconds = () => {
    const now = new Date()

    now.setSeconds(0)
    now.setMilliseconds(0)

    return now
}

const clampIfTodayBeforeNow = (value: Date) => {
    const now = getNowWithoutSeconds()

    if (isSameDay(value, now) && value.getTime() < now.getTime()) {
        return now
    }

    return value
}

const DateModal: FC<{
    confirmFn: (val: Date) => void
    maxDate?: Date
    noNextStep?: boolean
    noMinDate?: boolean
    startTab?: string
    deleteFn?: () => void
    newDateVal?: Date
    closeFn?: () => void
}> = ({
    confirmFn,
    noNextStep,
    noMinDate,
    maxDate,
    startTab = 'date',
    deleteFn,
    newDateVal,
    closeFn
}) => {
    const [activeTab, setActiveTab] = useState(startTab)
    const [date, setDate] = useState<Date>(() =>
        clampIfTodayBeforeNow(newDateVal || new Date())
    )

    const modalData = useModalContext()
    const { t } = useTranslation()

    const [scheduledCalendarPosts, setScheduledCalendarPosts] = useState<
        Array<{ scheduled: Date; type: postType }>
    >([])

    const getSelectedTime = (hours: number, minutes: number) => {
        setDate(prev => {
            const next = new Date(prev)

            next.setHours(hours)
            next.setMinutes(minutes)
            next.setSeconds(0)
            next.setMilliseconds(0)

            return clampIfTodayBeforeNow(next)
        })
    }
const closeModal = () => {
    if (closeFn) {
        closeFn()
        return
    }

    modalData.clearModal()
}
    const handleDateChange = (value: Date) => {
        setDate(prev => {
            const next = new Date(value)

            next.setHours(prev.getHours())
            next.setMinutes(prev.getMinutes())
            next.setSeconds(0)
            next.setMilliseconds(0)

            return clampIfTodayBeforeNow(next)
        })
    }

    const initializeScheduledContent = async () => {
        const data = await getScheduledContent()

        setScheduledCalendarPosts(
            data.map((content: ISchedule) => ({
                scheduled: new Date(content.schedule_date),
                type: content.type
            }))
        )
    }

    useEffect(() => {
        initializeScheduledContent()
    }, [])

    return (
        <div className='dateModal'>
            <div className='dateModal__body'>
                {activeTab === 'date' && (
                    <InlineDatePicker
                        onChange={(value) => handleDateChange(value as Date)}
                        value={date}
                        scheduledPosts={scheduledCalendarPosts}
                        dotsColor='red'
                        minDate={!noMinDate ? new Date() : undefined}
                        maxDate={maxDate}
                    />
                )}

                {activeTab === 'time' && (
                    <InlineTimePicker
                        getSelectedTime={getSelectedTime}
                        selectedDate={date}
                    />
                )}
            </div>

            <div className='dateModal__actions'>
                <Button
                    text={t('cancel')}
                    color='grey'
                    font='mont-14-normal'
                    width='13'
                    height='3'
                    customClass='dateModal_date__button'
                    clickFn={closeModal}
                />

                {deleteFn && (
                    <Button
                        text={t('delete')}
                        color='grey'
                        font='mont-14-normal'
                        width='13'
                        height='3'
                        customClass='dateModal_date__button'
                        clickFn={() => {
                            deleteFn()
                            closeModal()
                        }}
                    />
                )}

                {activeTab === 'date' && (
                    <Button
                        text={!noNextStep ? t('next') : t('save')}
                        color='black'
                        font='mont-14-normal'
                        width='13'
                        height='3'
                        customClass='dateModal_date__button'
                        clickFn={() => {
                            const nextDate = clampIfTodayBeforeNow(date)

                            setDate(nextDate)

                            if (!noNextStep) {
                                setActiveTab('time')
                            } else {
                                confirmFn(nextDate)
                                closeModal()
                            }
                        }}
                    />
                )}

                {activeTab === 'time' && (
                    <Button
                        text={t('save')}
                        color='black'
                        font='mont-14-normal'
                        width='13'
                        height='3'
                        customClass='dateModal_date__button'
                        clickFn={() => {
                            const finalDate = clampIfTodayBeforeNow(date)

                            confirmFn(finalDate)
                            closeModal()

                            if (window.location.pathname.includes('schedule')) {
                                modalData.addModal(
                                    'Schedule',
                                    <ScheduleContent date={finalDate} />,
                                    false
                                )
                            }
                        }}
                    />
                )}
            </div>
        </div>
    )
}

export default DateModal